import { compressChanges } from 'App/components/ListView/ListViewHelpers';
import ComparisonTypes from 'Resources/ComparisonTypes';
import { SearchAction } from 'Resources/AIChat';
import OrderFilters from 'App/babel/filterConfigs/Order';
import ClientFilters from 'App/babel/filterConfigs/Client';

type Filter = SearchAction['filters'][0];

const mergeRangeFilters = (filters: Filter[]): Filter[] => {
	const grouped: Record<string, Filter[]> = {};

	for (const filter of filters) {
		if (!grouped[filter.a]) grouped[filter.a] = [];
		grouped[filter.a].push(filter);
	}
	const merged: Filter[] = [];

	for (const [attr, attrFilters] of Object.entries(grouped)) {
		const lower = attrFilters.find(f => ['gte', 'gt'].includes(f.c));
		const upper = attrFilters.find(f => ['lte', 'lt'].includes(f.c));

		if (lower && upper) {
			const lowerVal = Array.isArray(lower.v) ? lower.v[0] : lower.v;
			const upperVal = Array.isArray(upper.v) ? upper.v[0] : upper.v;
			merged.push({ a: attr, c: 'bt', v: [lowerVal, upperVal] });
		} else {
			merged.push(...attrFilters);
		}
	}

	return merged;
};

const buildRange = <T>(
	comparisonCode: string | undefined,
	rawValue: string[],
	convert: (v: string) => T | null
): { start: T | null; end: T | null } => {
	let start: T | null = null;
	let end: T | null = null;

	if (comparisonCode === 'bt' && rawValue.length >= 2) {
		start = convert(rawValue[0]);
		end = convert(rawValue[1]);
	} else if (comparisonCode === 'gte' || comparisonCode === 'gt') {
		start = convert(rawValue[0]);
	} else if (comparisonCode === 'lte' || comparisonCode === 'lt') {
		end = convert(rawValue[0]);
	} else if (comparisonCode === 'eq') {
		const val = convert(rawValue[0]);
		start = val;
		end = val;
	}

	return { start, end };
};

const getEntityAttributes = (entityType: string) => {
	switch (entityType) {
		case 'Client':
			return ClientFilters();
		case 'Order':
			return OrderFilters();
		default:
			return {};
	}
};

const getPath = (entityType: string) => {
	switch (entityType) {
		case 'Order':
			return 'orders';
		case 'Client':
			return 'companies';
		default:
			return entityType.toLowerCase();
	}
};

const RANGE_OPERATORS = ['gt', 'gte', 'lt', 'lte', 'bt'];

export const createListUrl = (entityType: string, inputFilters: Filter[]) => {
	if (!inputFilters?.length) {
		return null;
	}

	const filters = mergeRangeFilters(inputFilters);

	const getComparisonName = (code: string) =>
		Object.keys(ComparisonTypes).find(key => ComparisonTypes[key as keyof typeof ComparisonTypes] === code);

	const attributeToFilterName: Record<string, string> = {};
	const entityAttributes = getEntityAttributes(entityType);

	Object.entries(entityAttributes).forEach(([filterName, attributeConfig]) => {
		const field = attributeConfig.field;
		if (field && !attributeToFilterName[field]) {
			attributeToFilterName[field] = filterName;
		}
	});

	const uiFilters: Record<string, any> = {};
	for (const filter of filters) {
		const attribute = filter.a;
		const comparisonCode = filter.c;
		const comparisonName = comparisonCode ? getComparisonName(comparisonCode) : undefined;

		if (!comparisonName) continue;

		const filterName = attributeToFilterName[attribute] || attribute;

		if (RANGE_OPERATORS.includes(comparisonCode) && Array.isArray(filter.v)) {
			const { start, end } = buildRange(comparisonCode, filter.v, v => v);
			const isDateFilter = /Date$/i.test(filterName);
			uiFilters[filterName] = {
				filterName,
				value: { preset: isDateFilter ? 'custom' : undefined, start, end },
				comparisonType: null
			};
			continue;
		}

		uiFilters[filterName] = {
			filterName,
			value: filter.v,
			comparisonType: comparisonName
		};
	}
	if (!Object.keys(uiFilters).length) {
		return null;
	}

	const fParam = encodeURIComponent(compressChanges({ filters: uiFilters }));

	return `#/${getPath(entityType)}?f=${fParam}`;
};
